const reportsPageEN = {
	header: {
		title: "Reports",
		subtitle: "How the business is doing, and who keeps coming back.",
		subtitleStaff: "Your own bookings and clients.",
	},
	tabs: {
		sales: "Sales",
		customers: "Customers",
	},
	// The strip that picks one section at a time on a phone.
	sections: {
		label: "Report sections",
		overview: "Overview",
		money: "Money",
		bookings: "Bookings",
		changes: "Changes",
	},
	range: {
		label: "Period",
		last7: "Last 7 days",
		last30: "Last 30 days",
		last90: "Last 90 days",
		thisMonth: "This month",
		lastMonth: "Last month",
		thisYear: "This year",
		custom: "Custom",
		from: "From",
		to: "To",
		apply: "Apply",
		covering: "{{days}} days",
	},
	granularity: {
		label: "Group by",
		day: "Day",
		week: "Week",
		month: "Month",
	},
	filters: {
		allStaff: "All staff",
		allSources: "All sources",
		staff: "Staff",
		source: "Source",
	},
	sources: {
		online: "Online",
		manual: "Manual",
		"walk-in": "Walk-in",
	},
	kpis: {
		revenue: "Revenue",
		revenueHint: "From completed bookings only.",
		projected: "Upcoming",
		projectedHint: "Booked but not yet completed.",
		averageTicket: "Average ticket",
		averageTicketHint: "Revenue divided by completed bookings.",
		customers: "Customers",
		customersHint: "Distinct people booked in this period.",
		cancellations: "Cancellations",
		cancellationsHint:
			"Cancellations made during this period, including ones for appointments still to come.",
		cancellationsUnavailableHint: "Cancellation detail couldn't be loaded this time.",
		cancellationsSub: "of {{total}} bookings",
		changes: "Changes",
		changesHint: "Bookings in this period that were moved to a different time or edited after they were made.",
		changesSub: "{{moved}} moved · {{edited}} edited",
		completed: "Completed",
	},
	bookings: {
		title: "How busy you were",
		subtitle: "Scheduled time, not measured",
		total: "Bookings",
		totalHint: "Every booking whose appointment fell in this period.",
		totalSub: "{{concluded}} done · {{upcoming}} upcoming · {{cancelled}} cancelled",
		hours: "Hours booked",
		hoursHint:
			"The time these appointments were scheduled to take. Nothing records when a service really started or finished, so an appointment that ran long or was never attended still counts its full slot.",
		hoursSub: "{{done}} already done",
		averageLength: "Average length",
		averageLengthHint: "Scheduled minutes per appointment, cancellations excluded.",
		averageLengthSub: "typically {{median}}",
		leadTime: "Booking notice",
		leadTimeHint:
			"How far ahead people book, taken from the middle of the range. Bookings entered for a time that had already passed are left out.",
		leadTimeSub: "how far ahead people book",
		visitsPerClient: "Visits per client",
		visitsPerClientHint: "Appointments divided by the number of different people. Above 1 means people are coming back.",
		visitsPerClientSub: "across {{clients}} clients",
		byDay: "Busiest days",
		byHour: "Busiest hours",
		hourShare: "{{share}}% of bookings",
		homeVisits: "Home visits",
		homeVisitsCount: "Appointments",
		homeVisitsShare: "{{share}}% of bookings",
		travelTime: "Travel time",
		empty: "Nothing to show yet.",
		days: {
			mon: "Monday",
			tue: "Tuesday",
			wed: "Wednesday",
			thu: "Thursday",
			fri: "Friday",
			sat: "Saturday",
			sun: "Sunday",
		},
		duration: {
			minutesShort: "{{n}} min",
			hoursShort: "{{n}}h",
			hoursMinutes: "{{h}}h {{m}}m",
			daysShort: "{{n}} days",
		},
	},
	cancellations: {
		title: "Cancellations",
		subtitle: "Cancellations made during this period",
		count: "Cancellations",
		countHint: "Made during this period, including ones for appointments still to come.",
		inPeriod: "from {{count}} cancellation(s)",
		shortNotice: "Under 2 hours' notice",
		shortNoticeHint: "The ones you had almost no chance to refill.",
		ofTotal: "of {{total}}",
		medianNotice: "Typical notice",
		medianNoticeHint: "The middle amount of warning you got across all cancellations in this period.",
		cohortRate: "Rate for this period's appointments",
		cohortRateHint:
			"Of the appointments booked into this period that have concluded, the share that was cancelled. This moves more slowly than the count above, because it only counts cancellations whose appointment also fell in this period.",
		cohortRatePendingHint:
			"Not enough appointments in this period have concluded yet to give a meaningful rate.",
		cohortRateSub: "{{cancelled}} of {{settled}} concluded",
		byLeadTime: "By notice given",
		byActor: "By who cancelled",
		activityUnavailable: "Cancellation detail isn't available yet. It appears once the new index finishes building.",
		lead: {
			under2h: "Under 2 hours",
			under24h: "2 to 24 hours",
			under72h: "1 to 3 days",
			over72h: "More than 3 days",
			unknown: "Unknown",
		},
		actors: {
			customer: "Customer",
			shop: "You or your staff",
			system: "Automatic",
			unknown: "Unknown",
		},
		notice: {
			afterStart: "After the start time",
			minutesNotice: "{{n}} min",
			hoursNotice: "{{n}} hours",
			daysNotice: "{{n}} days",
		},
	},
	modifications: {
		title: "Changes to bookings",
		subtitle: "Appointments moved or edited after they were made",
		count: "Bookings changed",
		countHint:
			"Changes made during this period — moved to a different time, or had their services edited — counted on the day the change was made, wherever the appointment ended up.",
		countSub: "{{moved}} moved · {{edited}} edited",
		rate: "Change rate",
		rateHint:
			"Changes made during this period, measured against the bookings this period held. The two sides count slightly different things — a change made now can move an appointment outside these dates — so read it as “this many changes against a book of this size”. Cancelled bookings are left out; those count as cancellations, not changes.",
		ratePendingHint: "Not enough bookings in this period to give a meaningful rate.",
		rateSub: "of {{eligible}} bookings",
		shortNotice: "Under 2 hours' notice",
		shortNoticeHint: "Changed with almost no warning before the time it was going to run.",
		medianNotice: "Typical notice",
		medianNoticeHint: "The middle amount of warning across every change in this period, measured against the time being given up.",
		typicalMove: "Typical move",
		typicalMoveHint: "How far the middle appointment travelled from the time it was first booked for.",
		typicalMoveSub: "{{forward}} earlier · {{back}} later",
		repeated: "Moved more than once",
		repeatedHint: "Bookings that have been rescheduled at least twice.",
		movedBeyond: "Moved past these dates",
		/* Not "By notice given": the cancellations panel directly above it in the
		   same tab already carries that title, and two identical headings a card
		   apart measuring different populations is how a report gets misread. */
		byNotice: "Notice before the change",
		byMove: "By how far it moved",
		byMoveHint:
			"Measured from the time the appointment was first booked for, so a booking moved twice shows the whole distance. “Time unchanged” is a booking whose services were edited without touching the clock.",
		empty: "Nothing to show yet.",
		none: "Nothing was moved or edited in this period.",
		shift: {
			noMove: "Time unchanged",
			sameDay: "Same day",
			withinWeek: "Within a week",
			overWeek: "More than a week",
			unknown: "Not recorded",
		},
	},
	chart: {
		title: "Revenue over time",
		empty: "No completed bookings in this period.",
		bookingsLabel: "bookings",
	},
	breakdown: {
		title: "Where the money came from",
		byService: "By service",
		byStaff: "By staff",
		bySource: "By source",
		service: "Service",
		staff: "Staff",
		source: "Source",
		bookings: "Bookings",
		revenue: "Revenue",
		share: "Share",
		unassigned: "Unassigned",
		empty: "Nothing to show yet.",
		/* The ranked lists stop at a handful of rows and put the rest behind a
		   dialog, so a shop with twenty staff gets a readable card instead of a
		   page that scrolls forever. */
		showAll: "Show all {{count}}",
		// Subtitle of the dialog that button opens, where "Show all 20" read as a
		// second, dead copy of the button rather than as a count.
		rowCount: "{{count}} entries",
		andMore: "+{{count}} more",
		other: "Everything else",
		otherSub: "{{count}} smaller entries combined",
		searchRows: "Search this list",
		noMatches: "Nothing matches that search.",
		close: "Close",
		totalRow: "Total",
	},
	quality: {
		title: "About these numbers",
		estimated:
			"{{amount}} of this total was priced from your current service list, not from the booking itself — it will change if you edit those prices.",
		missing: "{{count}} booking(s) had no price we could resolve and count as zero.",
		unpriced: "Services with no price set: {{list}}",
		truncated: "This period has more bookings than we can total in one go. Narrow the dates for an exact figure.",
		allSnapshot: "Every booking in this period carries its own recorded price.",
		unsettled:
			"{{count}} booking(s) in this period haven't happened yet, so they are left out of the cancellation rate until they do.",
		cancellationsUnavailable:
			"Cancellation detail couldn't be loaded this time. The rate above is still correct.",
		concludedByTime:
			"{{count}} booking(s) are counted as done because their time has passed — walk-ins and bookings added by hand are never marked complete automatically. Their money is included in Revenue.",
		missingLength:
			"{{count}} booking(s) have no usable start and end time, so they are left out of the hours and length figures.",
		cancellationsTruncated:
			"There were more cancellations in this period than we can total in one go. Narrow the dates for an exact figure.",
		/* Only shown when the forward scan failed. Normally changes that pushed an
		   appointment into the future ARE counted, which is the whole point of
		   that scan — see aggregateModifications. */
		modificationsForwardUnavailable:
			"We couldn't check bookings scheduled after these dates this time, so changes that moved an appointment into the future may be missing from the count.",
	},
	customers: {
		search: "Search name, email or phone",
		sort: "Sort by",
		sortLastVisit: "Last visit",
		sortVisits: "Visits",
		sortRevenue: "Total spent",
		sortName: "Name",
		name: "Client",
		visits: "Visits",
		lastVisit: "Last visit",
		spent: "Total spent",
		favorite: "Usual service",
		empty: "No clients yet.",
		emptyHint: "Once bookings come in, everyone who visits will be listed here.",
		emptySearch: "No clients match that search.",
		emptySearchHint: "Try part of a name, an email address or a phone number.",
		registered: "Has an account",
		guest: "Guest",
		anonymousBucket: "Unidentified walk-ins (grouped)",
		showing: "Showing {{shown}} of {{total}}",
		prev: "Previous",
		next: "Next",
		noName: "No name given",
		truncated: "You have more bookings than we can read in one go, so the oldest are left out of these totals.",
	},
	detail: {
		title: "Client history",
		tabs: {
			summary: "Summary",
			history: "Bookings",
		},
		visits: "Visits",
		completed: "Completed",
		cancelled: "Cancelled",
		upcoming: "Upcoming",
		spent: "Total spent",
		firstVisit: "First visit",
		lastVisit: "Last visit",
		favoriteService: "Usual service",
		favoriteStaff: "Usually sees",
		history: "Bookings",
		noHistory: "No bookings for this client.",
		close: "Close",
		estimatedFlag: "Estimated from current prices",
		contact: "Contact",
		email: "Email",
		phone: "Phone",
		noContact: "No contact details on file.",
		copy: "Copy",
		copied: "Copied",
	},
	status: {
		completed: "Completed",
		confirmed: "Upcoming",
		rescheduled: "Moved",
		in_progress: "In progress",
		cancelled: "Cancelled",
		unknown: "Unknown",
	},
	errors: {
		load: "We couldn't load this report. Please try again.",
		loadCustomers: "We couldn't load your client list. Please try again.",
		loadDetail: "We couldn't load this client's history. Please try again.",
		noShop: "No shop is linked to your account yet.",
		badRange: "Please choose a start date on or before the end date.",
	},
	retry: "Try again",
	info: "What this means",
};

export default reportsPageEN;
